import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Lock, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FavoriteButton from './FavoriteButton';

const MusicVideoRow = ({ title, content }) => {
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.8;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  if (!content || content.length === 0) return null;

  return (
    <div className="mb-8" data-testid="music-video-row">
      <h2 className="text-2xl font-bold text-foreground mb-4 px-4 md:px-8">{title}</h2>
      
      <div className="relative group">
        {/* Left Arrow */}
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-full w-12 bg-gradient-to-r from-background to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
          data-testid="scroll-left-button"
        >
          <div className="w-10 h-10 rounded-full bg-secondary/80 backdrop-blur-sm flex items-center justify-center hover:bg-electric/20 transition-colors">
            <ChevronLeft className="h-6 w-6 text-foreground" />
          </div>
        </button>

        {/* Content Container */}
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-hide space-x-4 px-4 md:px-8 pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {content.map((item) => (
            <div key={item.id} className="flex-shrink-0 w-72">
              <MusicVideoCard content={item} navigate={navigate} />
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-full w-12 bg-gradient-to-l from-background to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
          data-testid="scroll-right-button"
        >
          <div className="w-10 h-10 rounded-full bg-secondary/80 backdrop-blur-sm flex items-center justify-center hover:bg-electric/20 transition-colors">
            <ChevronRight className="h-6 w-6 text-foreground" />
          </div>
        </button>
      </div>
    </div>
  );
};

// Music Video Card with 16:9 aspect ratio
const MusicVideoCard = ({ content, navigate }) => {
  return (
    <div
      className="group relative cursor-pointer rounded-2xl overflow-hidden bg-white/5 backdrop-blur-2xl border border-white/20 transition-all duration-300 hover:scale-105 hover:border-electric/60 hover:shadow-2xl hover:shadow-electric/40 hover:bg-white/10"
      style={{ backdropFilter: 'blur(30px) saturate(180%)' }}
      onClick={() => navigate(`/content/${content.id}`)}
      data-testid={`music-video-card-${content.id}`}
    >
      {/* 16:9 Aspect Ratio Thumbnail */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={content.thumbnail}
          alt={content.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-electric/90 flex items-center justify-center backdrop-blur-sm">
              <Play className="h-6 w-6 text-background fill-background" />
            </div>
          </div>
        </div>

        {/* Premium Badge */}
        {content.is_premium && (
          <div className="absolute top-2 right-2 bg-electric/95 backdrop-blur-xl px-3 py-1.5 rounded-lg flex items-center space-x-1 border border-white/30 shadow-lg" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
            <Lock className="h-3 w-3 text-background" />
            <span className="text-xs font-semibold text-background">Premium</span>
          </div>
        )}

        {/* PPV Badge */}
        {content.is_ppv && (
          <div className="absolute top-2 left-2 bg-pulse/95 backdrop-blur-xl px-3 py-1.5 rounded-lg border border-white/30 shadow-lg" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
            <span className="text-xs font-semibold text-background">$4.99 PPV</span>
          </div>
        )}

        {/* Favorite Button */}
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <FavoriteButton contentId={content.id} size="sm" />
        </div>
      </div>

      {/* Content Info */}
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground line-clamp-1 mb-1">
          {content.title}
        </h3>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{content.year}</span>
          <div className="flex items-center space-x-1">
            <Star className="h-3 w-3 text-electric fill-electric" />
            <span>{content.rating.toFixed(1)}</span>
          </div>
        </div>
        
        {content.duration && (
          <p className="text-xs text-muted-foreground mt-1">{content.duration}</p>
        )}
      </div>
    </div>
  );
};

export default MusicVideoRow;
